/* eslint-disable no-unused-vars */
import store from "@/store";

const THREE = window.THREE;

// const POINT_PICK_RADIUS = 0.5; // Increased radius for better point detection

function getWorldSizeAtDistance(distance) {
	const camera = store.state.viewer.impl.camera;
	const aspect = camera.aspect;
	const worldHeight = 2 * Math.tan(THREE.Math.degToRad(camera.fov / 2)) * distance;
	return worldHeight * aspect; // Return width (for horizontal dominance)
}

function intersectsBoundingVolume(raycaster, object) {
	if (!object.geometry) return true;

	if (!object.geometry.boundingSphere) {
		object.geometry.computeBoundingSphere();
	}

	const sphere = object.geometry.boundingSphere.clone();
	sphere.applyMatrix4(object.matrixWorld);

	return raycaster.ray.intersectSphere(sphere, new THREE.Vector3()) !== null;
}

function findClosestPointIntersection(ray, points, object) {
    let closestIntersection = null;
    let minProjectionDistance = Infinity;
    let minPerpendicularDistance = Infinity;
    const tempVec = new THREE.Vector3();

    points.forEach((point, index) => {
        const toPoint = tempVec.subVectors(point, ray.origin);
        const rayDirection = ray.direction.normalize(); // Ensure normalized
        
        // Calculate projection length (distance from origin to projected point)
        const projectionLength = toPoint.dot(rayDirection);
        
        // Skip points behind the ray origin
        if (projectionLength < 0) return;

        // Projected point on ray
        const projectedPoint = ray.origin.clone().add(rayDirection.multiplyScalar(projectionLength));
        
        // Perpendicular distance from point to ray
        const distanceToRay = point.distanceTo(projectedPoint);

		const POINT_PICK_RADIUS = 1; // 5-pixel tolerance

        if (distanceToRay <= POINT_PICK_RADIUS) {
            // Prioritize smaller projection length first, then smaller distanceToRay
            if (
                projectionLength < minProjectionDistance ||
                (projectionLength === minProjectionDistance && distanceToRay < minPerpendicularDistance)
            ) {
                minProjectionDistance = projectionLength;
                minPerpendicularDistance = distanceToRay;
                closestIntersection = {
                    point: point.clone().applyMatrix4(object.matrixWorld),
                    distance: projectionLength,
                    distanceToRay,
                    object,
                    index
                };
            }
        }
    });

    return closestIntersection;
}

export function intersectPointCloud(ray, pointCloud) {
	if (!pointCloud.geometry) return null;

	const geometry = pointCloud.geometry;
	const position = geometry.attributes.position;
	if (!position) return null;

	const localRay = ray.clone();
	const inverseMatrix = new THREE.Matrix4().copy(pointCloud.matrixWorld).invert();
	localRay.applyMatrix4(inverseMatrix);

	const points = [];
	for (let i = 0; i < position.count; i++) {
		points.push(new THREE.Vector3().fromBufferAttribute(position, i));
	}

	return findClosestPointIntersection(localRay, points, pointCloud);
}

export function intersectFirstRecursively(raycaster, object) {
	// Skip if no bounding volume intersection
	if (!intersectsBoundingVolume(raycaster, object)) {
		return null;
	}

	// Recursively check children
	if (object.children && object.children.length > 0) {
		for (const child of object.children) {
			let intersection = intersectFirstRecursively(raycaster, child);
			if (intersection && intersection.object.children.length === 0) {
				return intersection;
			}
		}
	}

	// Handle point cloud
	if (object instanceof THREE.PointCloud && !object.children?.length) {
		return intersectPointCloud(raycaster.ray, object);
	}

	return null;
}

export function intersectRecursively(raycaster, object, intersections = []) {
	// Skip if no bounding volume intersection
	if (!intersectsBoundingVolume(raycaster, object)) {
		return intersections;
	}

	// Recursively check children
	if (object.children && object.children.length > 0) {
		for (const child of object.children) {
			intersectRecursively(raycaster, child, intersections);
		}
	}

	// Handle point cloud
	if (object instanceof THREE.PointCloud) {
		const intersection = intersectPointCloud(raycaster.ray, object);
		if (intersection) {
			intersections.push(intersection);
		}
	}

	return intersections;
}